import React from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ESG_ICONS } from '../../constants/esgIcons';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import type { CarbonAvoidanceStreamRow } from '../../types/carbon-avoidance-report.type';
import { carbonMaterialLabel } from '../../utils/carbonAvoidance.util';
import ChartPanel from '../charts/ChartPanel';
import EmptyState from '../EmptyState';

interface CarbonAvoidanceByStreamTableProps {
  rows: CarbonAvoidanceStreamRow[];
  title?: string;
  compact?: boolean;
  fillHeight?: boolean;
  caption?: React.ReactNode;
  id?: string;
}

/** Avoided emissions and mass by catalog material, with EF versions (dashboard Carbon tab). */
const CarbonAvoidanceByStreamTable: React.FC<CarbonAvoidanceByStreamTableProps> = ({
  rows,
  title,
  fillHeight = false,
  caption,
  id,
}) => {
  const { t } = useTranslation();
  const fmt = useNumberFormat();
  const resolvedTitle = title ?? t('carbonAvoidance.byStreamTitle');

  return (
    <ChartPanel
      id={id}
      title={resolvedTitle}
      icon={ESG_ICONS.avoidedEmissions}
      caption={caption}
      sx={fillHeight ? { height: '100%' } : undefined}
    >
      {!rows.length ? (
        <EmptyState title={t('dashboard.alerts.noCarbonByStream')} variant="inline" minHeight={140} />
      ) : (
        <TableContainer sx={{ mx: { xs: -2, md: -2.5 }, width: 'auto', overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 420 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ paddingInlineStart: { xs: 2, md: 2.5 } }}>{t('carbonAvoidance.stream')}</TableCell>
                <TableCell align="right">{t('carbonAvoidance.avoidedTco2e')}</TableCell>
                <TableCell align="right">{t('carbonAvoidance.weightTonnes')}</TableCell>
                <TableCell sx={{ paddingInlineEnd: { xs: 2, md: 2.5 } }}>{t('carbonAvoidance.efVersionsUsed')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.material_label} hover>
                  <TableCell sx={{ paddingInlineStart: { xs: 2, md: 2.5 }, fontWeight: 500 }}>
                    {carbonMaterialLabel(row.material_label, t('carbonAvoidance.unclassified'))}
                  </TableCell>
                  <TableCell align="right">
                    <bdi>{fmt.metric('tco2e', row.avoided_tco2e).text}</bdi>
                  </TableCell>
                  <TableCell align="right">
                    <bdi>{fmt.metric('mass_t', row.weight_tonnes).text}</bdi>
                  </TableCell>
                  <TableCell sx={{ paddingInlineEnd: { xs: 2, md: 2.5 } }}>
                    <Box
                      component="span"
                      sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.75rem', color: 'text.secondary' }}
                    >
                      {(row.emission_factor_versions_used ?? [])
                        .map((ef) => `${ef.version_label} · ${t('carbonAvoidance.runsCount', { count: ef.run_count })}`)
                        .join('; ') || '—'}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </ChartPanel>
  );
};

export default CarbonAvoidanceByStreamTable;
